import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faArrowRight, faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useTranslation } from 'react-i18next';

const PDF_URL = '/CBAM-Onboarding-3rd-Country-Operators.pdf';
const PDF_FILENAME = 'CBAM Onboarding - 3rd Country Operators - Carbon Glance.pdf';

// Same bundled PDF.js viewer used by the Report preview (PreviewPane.jsx).
// We don't load PDFs via `<iframe src=...pdf>` directly because browsers
// disagree on whether to render inline vs. trigger a download.
const VIEWER_URL = '/pdfjs/viewer.html';

const OperatorOnboardingPdfStep = ({ onContinue }) => {
  const { t } = useTranslation('onboarding');
  const iframeRef = useRef(null);
  const [viewerError, setViewerError] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timer = null;

    const tryOpen = () => {
      if (cancelled) return;
      const win = iframe.contentWindow;
      const api = win && win.pdfApp;
      if (api && typeof api.open === 'function') {
        api.open(PDF_URL).catch(() => {
          if (!cancelled) setViewerError(true);
        });
        return;
      }
      attempts += 1;
      if (attempts >= 200) {
        // ~5s — viewer module never finished loading
        setViewerError(true);
        return;
      }
      timer = setTimeout(tryOpen, 25);
    };

    const handleLoad = () => {
      attempts = 0;
      tryOpen();
    };

    iframe.addEventListener('load', handleLoad);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      iframe.removeEventListener('load', handleLoad);
    };
  }, []);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = PDF_URL;
    link.download = PDF_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="text-center mb-6">
        <div className="inline-flex w-12 h-12 rounded-2xl bg-primaryColor/10 text-primaryColor items-center justify-center mb-4">
          <FontAwesomeIcon
            icon={faFilePdf}
            className="text-xl"
          />
        </div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primaryColor/10 text-primaryColor text-[10px] font-bold uppercase tracking-wider mb-3">
          {t('common:fields.optional', 'Optional')}
        </span>
        <h2 className="text-2xl font-semibold text-slate-800 m-0">
          {t('pdf.yourGuide', 'Your CBAM onboarding guide')}
        </h2>
        <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">
          {t(
            'pdf.guideIntro',
            "We've put together a short guide that walks you through the onboarding steps and what CBAM data you'll need. Preview it below or download a copy for later.",
          )}
        </p>
      </div>

      <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 mb-5">
        {viewerError ? (
          <div className="h-[480px] flex flex-col items-center justify-center text-center px-6 gap-3 bg-white">
            <FontAwesomeIcon
              icon={faFilePdf}
              className="text-3xl text-slate-300"
            />
            <div className="text-sm text-slate-600">
              {t('pdf.previewFailed', "The inline preview couldn't load.")}
            </div>
            <CgRadixButton
              variant="outline"
              size="md"
              icon={faFileArrowDown}
              onClick={handleDownload}
            >
              {t('pdf.downloadInstead', 'Download PDF instead')}
            </CgRadixButton>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={VIEWER_URL}
            title={t('pdf.operatorGuide', 'CBAM Onboarding guide for 3rd country operators')}
            className="block w-full"
            style={{ height: '600px', border: 'none' }}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <CgRadixButton
          variant="outline"
          size="md"
          icon={faFileArrowDown}
          onClick={handleDownload}
        >
          {t('pdf.download', 'Download PDF')}
        </CgRadixButton>
        <CgRadixButton
          variant="primary"
          size="lg"
          onClick={onContinue}
          icon={faArrowRight}
        >
          {t('actions.continueToPlatform', 'Continue to platform')}
        </CgRadixButton>
      </div>
    </div>
  );
};

OperatorOnboardingPdfStep.propTypes = {
  onContinue: PropTypes.func.isRequired,
};

export default OperatorOnboardingPdfStep;
